import React from "react";
import { Link } from "react-router-dom";
import MarketingHeader from "@/components/Layout/MarketingHeader";
import { t } from "@/i18n";
import { ArrowRight } from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/4ab3c465-caf6-4699-80b7-ac0418042b65/images/65b553d9446bb8b6de573ff329266334a0fd099a181225ed85067687e1c47c10.png";
const COCKTAIL_IMG = "https://static.prod-images.emergentagent.com/jobs/4ab3c465-caf6-4699-80b7-ac0418042b65/images/c81f20cb29516a9450bd4501519bd6421b8df6fd9469fa2b7983e1c9305b2ad6.png";
const INTERIOR_IMG = "https://images.unsplash.com/photo-1766832255363-c9f060ade8b0?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[var(--bg-rooftop)] text-[var(--text-primary)]">
      <MarketingHeader />

      {/* ============================== HERO ============================== */}
      <section className="relative min-h-screen flex items-center pt-28 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--bg-rooftop)]/40 via-[var(--bg-rooftop)]/75 to-[var(--bg-rooftop)]" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 py-24 w-full">
          <div className="text-[10px] tracking-[0.42em] uppercase text-[var(--gold-warm)] mb-10 animate-fade-up" data-testid="hero-eyebrow">
            {t("marketing.hero.eyebrow")}
          </div>
          <h1 className="font-editorial italic font-light text-5xl sm:text-7xl lg:text-[6rem] leading-[1.02] tracking-[-0.01em] text-[var(--text-primary)] mb-3 animate-fade-up-delay-1">
            {t("marketing.hero.title_1")}
          </h1>
          <h1 className="font-editorial italic font-light text-5xl sm:text-7xl lg:text-[6rem] leading-[1.02] tracking-[-0.01em] text-[var(--text-secondary)] mb-3 animate-fade-up-delay-2">
            {t("marketing.hero.title_2")}
          </h1>
          <h1 className="font-editorial italic font-light text-5xl sm:text-7xl lg:text-[6rem] leading-[1.02] tracking-[-0.01em] text-[var(--gold-cool)] mb-12 animate-fade-up-delay-3">
            {t("marketing.hero.title_3")}
          </h1>
          <p className="text-[var(--text-secondary)] text-base sm:text-lg max-w-2xl leading-relaxed mb-12 font-light animate-fade-up-delay-3">
            {t("marketing.hero.subtitle")}
          </p>
          <div className="flex flex-wrap items-center gap-6 animate-fade-up-delay-3">
            <Link to="/register" className="btn-primary inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase" data-testid="hero-cta-primary">
              {t("marketing.hero.cta_primary")} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.6} />
            </Link>
            <a href="#philosophy" className="text-[11px] tracking-[0.24em] uppercase text-[var(--text-muted)] hover:text-[var(--gold)] transition-colors duration-700" data-testid="hero-cta-secondary">
              {t("marketing.hero.cta_secondary")} →
            </a>
          </div>
        </div>
      </section>

      {/* ============================== PHILOSOPHY ============================== */}
      <section id="philosophy" className="relative py-36 px-6 lg:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="text-[10px] tracking-[0.42em] uppercase text-[var(--gold-warm)] mb-8">{t("marketing.philosophy.eyebrow")}</div>
          <h2 className="font-editorial italic font-light text-4xl sm:text-6xl leading-[1.05] tracking-tight text-[var(--text-primary)] mb-14">
            {t("marketing.philosophy.title")}
          </h2>
          <div className="space-y-7 text-[var(--text-secondary)] text-lg leading-[1.75] font-light max-w-2xl">
            <p data-testid="philosophy-1">{t("marketing.philosophy.body_1")}</p>
            <p data-testid="philosophy-2">{t("marketing.philosophy.body_2")}</p>
            <p data-testid="philosophy-3">{t("marketing.philosophy.body_3")}</p>
          </div>
        </div>
      </section>

      {/* ============================== ATMOSPHERE SPLIT ============================== */}
      <section id="product" className="relative py-24 px-6 lg:px-12">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative aspect-[4/5] overflow-hidden rounded-sm">
            <img src={COCKTAIL_IMG} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 ring-1 ring-inset ring-white/[0.04]" />
          </div>
          <div>
            <h2 className="font-editorial italic font-light text-3xl sm:text-5xl leading-[1.1] tracking-tight mb-8 text-[var(--text-primary)]">
              Wenn der Tisch noch nicht bereit ist —<br />
              <span className="text-[var(--gold-cool)]">die Lounge ist es.</span>
            </h2>
            <p className="text-[var(--text-secondary)] leading-[1.75] mb-10 font-light text-[15px] max-w-md">
              Der ruhige Kern von SONARA: Gäste warten nicht an der Tür, sondern in Ihrer Lounge mit einem Glas in der Hand. Sobald der Tisch frei ist, übergibt das Team den Gast diskret. Die Erfahrung bleibt vom ersten Schritt bis zum letzten Gang ohne Bruch.
            </p>
          </div>
        </div>
      </section>

      {/* ============================== CANVAS — was alles enthalten ist ============================== */}
      <section className="relative py-32 px-6 lg:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="text-[10px] tracking-[0.42em] uppercase text-[var(--gold-warm)] mb-8">{t("marketing.canvas.eyebrow")}</div>
          <h2 className="font-editorial italic font-light text-4xl sm:text-5xl leading-[1.1] tracking-tight text-[var(--text-primary)] mb-10">
            {t("marketing.canvas.title")}
          </h2>
          <p className="text-[var(--text-secondary)] leading-[1.75] font-light text-[15px] mb-12 max-w-2xl">
            {t("marketing.canvas.body")}
          </p>
          <ul className="space-y-4 max-w-xl">
            {t("marketing.canvas.lines").map((line, i) => (
              <li
                key={i}
                data-testid={`canvas-line-${i}`}
                className="font-editorial italic text-[var(--text-primary)] text-[1.6rem] leading-[1.4] tracking-tight"
              >
                {line}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============================== EDITION & TRIAL ============================== */}
      <section id="trial" className="relative py-36 px-6 lg:px-12">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-[1fr_360px] gap-16 items-start">
          <div>
            <div className="text-[10px] tracking-[0.42em] uppercase text-[var(--gold-warm)] mb-8">{t("marketing.edition.eyebrow")}</div>
            <h2 className="font-editorial italic font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[var(--text-primary)] mb-3">
              {t("marketing.edition.title")}
            </h2>
            <h2 className="font-editorial italic font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[var(--gold-cool)] mb-12">
              {t("marketing.edition.title_2")}
            </h2>
            <div className="space-y-6 text-[var(--text-secondary)] text-lg leading-[1.75] font-light max-w-xl">
              <p data-testid="edition-1">{t("marketing.edition.body_1")}</p>
              <p data-testid="edition-2">{t("marketing.edition.body_2")}</p>
            </div>
          </div>

          {/* Trial card — quiet showroom card */}
          <aside
            className="surface-1 rounded-md p-10 self-stretch flex flex-col"
            style={{
              background: "var(--bg-velvet)",
              boxShadow: "0 1px 0 rgba(184,150,86,0.10) inset, 0 60px 120px -40px rgba(0,0,0,0.7)",
            }}
            data-testid="trial-card"
          >
            <div className="text-[10px] tracking-[0.32em] uppercase text-[var(--text-dim)] mb-6">{t("marketing.edition.trial_label")}</div>
            <div className="font-editorial italic font-light text-[5rem] leading-none text-[var(--gold)] mb-2" data-testid="trial-value">
              {t("marketing.edition.trial_value")}
            </div>
            <div className="text-[11px] tracking-[0.32em] uppercase text-[var(--text-muted)] mb-10">kostenlos</div>
            <p className="text-[13px] text-[var(--text-muted)] leading-relaxed font-light mb-10">
              {t("marketing.edition.trial_hint")}
            </p>
            <Link
              to="/register"
              className="btn-primary text-[11px] tracking-[0.22em] uppercase inline-flex items-center justify-center gap-2 mt-auto"
              data-testid="trial-cta"
            >
              {t("marketing.hero.cta_primary")} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.6} />
            </Link>
          </aside>
        </div>
      </section>

      {/* ============================== FINAL CTA ============================== */}
      <section className="relative py-36 px-6 lg:px-12 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={INTERIOR_IMG} alt="" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--bg-rooftop)] via-[var(--bg-rooftop)]/85 to-[var(--bg-rooftop)]" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <h2 className="font-editorial italic font-light text-4xl sm:text-6xl leading-[1.05] tracking-tight mb-6 text-[var(--text-primary)]" data-testid="final-cta-title">
            {t("marketing.cta.title")}
          </h2>
          <p className="text-[var(--text-secondary)] mb-12 font-light text-lg leading-relaxed">{t("marketing.cta.subtitle")}</p>
          <div className="flex flex-wrap gap-5 justify-center">
            <Link to="/register" className="btn-primary inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase" data-testid="final-cta-primary">
              {t("marketing.cta.primary")} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.6} />
            </Link>
            <Link to="/r/demo" className="text-[11px] tracking-[0.24em] uppercase text-[var(--text-muted)] hover:text-[var(--gold)] transition-colors duration-700 self-center" data-testid="final-cta-secondary">
              {t("marketing.cta.secondary")} →
            </Link>
          </div>
        </div>
      </section>

      {/* ============================== FOOTER ============================== */}
      <footer className="px-6 lg:px-12 py-12 relative">
        <span aria-hidden className="absolute top-0 left-12 right-12 h-px brass-line opacity-60" />
        <div className="max-w-6xl mx-auto flex flex-wrap items-end justify-between gap-8 text-[var(--text-dim)]">
          <div>
            <div className="font-heading font-extralight text-[1.6rem] tracking-[0.55em] text-[var(--text-primary)]">SONARA</div>
            <div className="text-[10px] tracking-[0.32em] uppercase text-[var(--text-muted)] mt-2">{t("marketing.footer.tagline")}</div>
          </div>
          <div className="text-[11px] tracking-[0.22em] uppercase">© {new Date().getFullYear()} · {t("marketing.footer.rights")}</div>
          <div className="flex gap-8 text-[11px] tracking-[0.22em] uppercase">
            <Link to="/login" className="hover:text-[var(--gold)] transition-colors duration-700">{t("marketing.nav.login")}</Link>
            <Link to="/r/demo" className="hover:text-[var(--gold)] transition-colors duration-700">Buchung ansehen</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
